import type {
  ContactSortDto,
  SaveContactViewRequest,
  SaveContactViewResponse,
  SegmentDefinitionDto,
} from '@carbonwebmail/contracts'
import { SaveViewDialog as AskForName } from '../../ui/SaveViewDialog.js'

/**
 * Keeping the way the contacts board is arranged, as a tab.
 *
 * **A view, not a segment.** This used to hand the filter to the segment
 * builder, so narrowing a list and keeping it created an audience a campaign
 * could be sent to. A view says what somebody is looking at; segments are made
 * on the Segments screen.
 *
 * The asking is `ui/SaveViewDialog`, shared with the segments board. What is
 * here is only what a *contacts* view holds — the filter, the order, the hidden
 * columns — and how to say that in words.
 */
export function SaveViewDialog({
  rules,
  sort,
  hidden,
  onSave,
  onClose,
}: {
  readonly rules: SegmentDefinitionDto['children']
  readonly sort: ContactSortDto | undefined
  readonly hidden: readonly string[]
  readonly onSave: (view: SaveContactViewRequest) => Promise<SaveContactViewResponse>
  readonly onClose: () => void
}) {
  return (
    <AskForName
      keeps={described(rules.length, sort !== undefined, hidden.length)}
      onClose={onClose}
      onSave={async (name) => {
        const result = await onSave({
          name,
          definition: { match: 'all', children: rules },
          ...(sort === undefined ? {} : { sort }),
          hidden: [...hidden],
        })
        return result.status
      }}
    />
  )
}

/** What the view will hold, in words rather than three counts. */
function described(rules: number, ordered: boolean, hidden: number): string {
  const parts = [
    rules === 0 ? 'no filter' : rules === 1 ? '1 condition' : `${rules} conditions`,
    ordered ? 'the current order' : 'the board’s own order',
  ]
  if (hidden > 0) parts.push(hidden === 1 ? '1 hidden column' : `${hidden} hidden columns`)
  return parts.join(', ')
}
